// Core
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

// Components
import {
  Button,
  Crumbs,
  DateRangePopover,
  PageHead,
  SectionCard,
  SideTabs,
  Tabs,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import DeleteRecordsModal from "components/Modals/DeleteRecordsModal";

// Redux
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  selectTimeSelector,
  updateTimeSelector,
  openNotificationPopup,
} from "state/application/applicationSlice";
import {
  useGetEMSConfigsQuery,
  useGetAvailableEMSModulesQuery,
  useExportEMSSetpointsMutation,
  useDeleteEMSSetpointsMutation,
} from "state/communities/communitiesApiSlice";

// Generic module view - capability-driven, no hardcoded per-module components
import ModuleView from "./components/ModuleView";

const STATUS_COLOR = {
  SUCCESS: "var(--success)",
  FAILED: "var(--danger)",
  RUNNING: "var(--warning)",
};

function ManageEnergyManagement() {
  const { t } = useTranslation(["community", "sidenav"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;
  const { startDate, endDate, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [currentlySelected, setCurrentlySelected] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [exportSetpoints, { isLoading: isExporting }] = useExportEMSSetpointsMutation();
  const [deleteSetpoints, { isLoading: isDeleting }] = useDeleteEMSSetpointsMutation();

  const { data: emsConfigs, isLoading: isLoadingConfigs } = useGetEMSConfigsQuery(communityId, {
    skip: !communityId,
  });
  const { data: modulesData, isLoading: isLoadingModules } = useGetAvailableEMSModulesQuery(
    communityId,
    { skip: !communityId }
  );

  const activeConfigs = useMemo(
    () => (emsConfigs || []).filter((config) => config.is_active),
    [emsConfigs]
  );
  const availableModules = modulesData?.modules || [];
  const getModuleInfo = (moduleType) =>
    availableModules.find((m) => m.module_type === moduleType) || {};

  // Default the selection to the first active module
  useEffect(() => {
    if (activeConfigs.length > 0 && !currentlySelected) {
      setCurrentlySelected(activeConfigs[0].module_type);
    }
  }, [activeConfigs, currentlySelected]);

  // Reset the tab when the module changes
  useEffect(() => {
    setTabValue(0);
  }, [currentlySelected]);

  // Tabs (with icons) driven by the selected module capabilities. Order matches
  // the numeric tabValue contract expected by ModuleView.
  const getTabs = (capabilities) => {
    const hasSetpoints =
      capabilities.includes("setpoint_calculation") ||
      capabilities.includes("local_optimization") ||
      capabilities.includes("external_setpoint_reception");
    const hasOffers =
      capabilities.includes("offer_generation") ||
      capabilities.includes("external_offer_reception");
    const hasAnyChart = [
      "setpoint_chart",
      "member_energy_chart",
      "baseline_chart",
      "opportunity_cost_chart",
      "scenario_chart",
      "retail_transactions_chart",
      "bilateral_transactions_chart",
      "liquid_bat_chart",
      "ev_chart",
      "bat_soc_chart",
      "cost_comparison_chart",
      "solar_allocation_chart",
    ].some((c) => capabilities.includes(c));

    const offers = { label: t("unified_ems.tab_offers"), icon: "sell" };
    const setpoints = { label: t("unified_ems.tab_setpoints"), icon: "table_rows" };
    const charts = { label: t("unified_ems.tab_chart"), icon: "show_chart" };

    let list = [];
    if (hasSetpoints && hasOffers)
      list = hasAnyChart ? [offers, setpoints, charts] : [offers, setpoints];
    else if (hasSetpoints && hasAnyChart) list = [setpoints, charts];
    return list.map((tb, i) => ({ ...tb, id: String(i) }));
  };

  // YYYY-MM-DD → ISO datetime for API queries
  const startDatetime = startDate
    ? DateTime.fromISO(startDate).startOf("day").toFormat("yyyy-MM-dd'T'HH:mm:ss")
    : undefined;
  const endDatetime = endDate
    ? DateTime.fromISO(endDate).endOf("day").toFormat("yyyy-MM-dd'T'HH:mm:ss")
    : undefined;

  const handleConfirmDelete = async ({ startDate: dStart, endDate: dEnd }) => {
    try {
      await deleteSetpoints({
        communityId,
        moduleType: currentlySelected,
        startDatetime: dStart ? `${dStart}T00:00:00` : undefined,
        endDatetime: dEnd ? `${dEnd}T23:59:59` : undefined,
      }).unwrap();
      dispatch(
        openNotificationPopup({
          notification: t("unified_ems.delete_success_desc"),
          type: "success",
        })
      );
      setDeleteModalOpen(false);
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err?.message ?? String(err), type: "error" }));
    }
  };

  const handleExportSetpoints = async () => {
    try {
      const blob = await exportSetpoints({
        communityId,
        moduleType: currentlySelected,
        startDatetime,
        endDatetime,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `setpoints_${currentlySelected}_${startDate || "all"}_${
        endDate || "all"
      }.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(
        openNotificationPopup({ notification: err?.data?.detail ?? String(err), type: "error" })
      );
    }
  };

  const crumbs = (
    <Crumbs
      items={[
        { label: community?.name || "—" },
        { label: t("energymanagement", { ns: "sidenav" }) },
      ]}
    />
  );

  const isLoading = isLoadingConfigs || isLoadingModules;

  if (isLoading) {
    return (
      <div>
        {crumbs}
        <div className="flex justify-center items-center min-h-[320px] text-ink-3 text-[13px]">
          {t("loading", { defaultValue: "A carregar…" })}
        </div>
      </div>
    );
  }

  // Empty state — no active modules
  if (activeConfigs.length === 0) {
    return (
      <div>
        <HelpFloatingButtonV2 helpScreen="energy-management" />
        {crumbs}
        <PageHead
          title={t("energymanagement", { ns: "sidenav" })}
          subtitle={t("unified_ems.page_subtitle")}
        />
        <div className="mx-auto flex flex-col gap-rec" style={{ maxWidth: 640 }}>
          {/* Hero (no card) — verbatim layout from the design's EmsEmptyState */}
          <div className="text-center" style={{ padding: "36px 24px 8px" }}>
            <div
              className="mx-auto flex items-center justify-center"
              style={{
                width: 76,
                height: 76,
                borderRadius: 22,
                marginBottom: 22,
                background: "var(--warning-soft)",
                color: "var(--warning)",
              }}
            >
              <Icon name="bolt" fill style={{ fontSize: 40 }} />
            </div>
            <h2
              className="text-ink m-0 mb-[10px] font-bold tracking-[-0.4px]"
              style={{ fontSize: 23 }}
            >
              {t("unified_ems.no_active_modules")}
            </h2>
            <p
              className="text-ink-2 mx-auto mb-6 text-[14px] leading-[1.6]"
              style={{ maxWidth: 460 }}
            >
              {t("unified_ems.no_active_modules_description")}
            </p>
            <Button onClick={() => navigate(`/communities/${communityId}/configuration`)}>
              <Icon name="settings" />
              {t("unified_ems.go_to_settings")}
            </Button>
          </div>

          {/* Info card */}
          <div className="rec-card rec-card-pad text-left">
            <div className="flex items-center gap-[9px] text-[13.5px] font-bold text-ink mb-4">
              <Icon name="help" style={{ color: "var(--accent)" }} />
              {t("unified_ems.what_are_ems_modules")}
            </div>
            <ul className="list-none m-0 p-0 flex flex-col gap-[13px]">
              {["ems_benefit_1", "ems_benefit_2", "ems_benefit_3", "ems_benefit_4"].map((k) => (
                <li
                  key={k}
                  className="flex items-start gap-3 text-[13.5px] leading-[1.5] text-ink-2"
                >
                  <span
                    className="shrink-0 flex items-center justify-center mt-[1px]"
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "var(--accent-soft)",
                      color: "var(--accent)",
                    }}
                  >
                    <Icon name="check" style={{ fontSize: 13 }} />
                  </span>
                  <span>{t(`unified_ems.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const railItems = activeConfigs.map((config) => {
    const info = getModuleInfo(config.module_type);
    const dotColor = STATUS_COLOR[config.last_run_status];
    return {
      id: config.module_type,
      icon: info.icon || "electric_bolt",
      label: (
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className="truncate">
            {info.display_name || config.module_type.replace(/_/g, " ")}
          </span>
          {dotColor && (
            <span
              className="inline-block w-[7px] h-[7px] rounded-full shrink-0"
              style={{ background: dotColor }}
            />
          )}
        </span>
      ),
    };
  });

  const selectedConfig = activeConfigs.find((c) => c.module_type === currentlySelected);
  const selectedInfo = getModuleInfo(currentlySelected);
  const capabilities = selectedInfo.capabilities || [];
  const tabs = getTabs(capabilities);

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="energy-management" />
      {crumbs}
      <PageHead
        title={t("energymanagement", { ns: "sidenav" })}
        subtitle={t("unified_ems.page_subtitle")}
        actions={
          <div className="flex items-center gap-[10px] flex-wrap">
            <DateRangePopover
              value={{ start: startDate, end: endDate }}
              onChange={(v) =>
                dispatch(
                  updateTimeSelector({ startDate: v.start, endDate: v.end, horizon, isExpanded })
                )
              }
            />
            <span className="w-px self-stretch bg-border my-1 mx-[2px]" aria-hidden="true" />
            <Button variant="ghost" onClick={() => setDeleteModalOpen(true)}>
              <Icon name="delete" />
              {t("unified_ems.delete_action")}
            </Button>
            <Button variant="ghost" disabled={isExporting} onClick={handleExportSetpoints}>
              <Icon name="download" />
              {isExporting ? t("unified_ems.exporting") : t("unified_ems.export")}
            </Button>
          </div>
        }
      />

      <SideTabs items={railItems} active={currentlySelected} onChange={setCurrentlySelected}>
        {selectedConfig && (
          <SectionCard
            icon={selectedInfo.icon || "electric_bolt"}
            title={selectedInfo.display_name || currentlySelected?.replace(/_/g, " ")}
            sub={selectedInfo.description || t("unified_ems.setpoints_for_module")}
          >
            {tabs.length > 0 && (
              <Tabs
                tabs={tabs}
                active={String(tabValue)}
                onChange={(id) => setTabValue(Number(id))}
              />
            )}

            <ModuleView
              moduleType={selectedConfig.module_type}
              capabilities={capabilities}
              tabValue={tabValue}
              startDatetime={startDatetime}
              endDatetime={endDatetime}
            />
          </SectionCard>
        )}
      </SideTabs>

      <DeleteRecordsModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title={t("unified_ems.delete_setpoints")}
      />
    </div>
  );
}

export default ManageEnergyManagement;
